<template>
  <f7-page no-swipeback>
    <f7-navbar>
      <f7-nav-left>
        <f7-link icon-ios="f7:menu" icon-md="material:menu" panel-open="left"/>
      </f7-nav-left>
      <f7-nav-title>{{$t('title.company')}}</f7-nav-title>
    </f7-navbar>
    <f7-list accordion-list>
      <f7-list-item :title="$tc('label.user', 2)" :link="links.users"/>
      <f7-list-item accordion-item :title="$t('title.companyInfo')">
        <f7-accordion-content>
          <f7-block>
            <f7-block-title>{{company.name}}</f7-block-title>
            <f7-card>
              <f7-card-content>{{$tc('label.activeUsers', {count: company.activeUsers})}}</f7-card-content>
            </f7-card>
            <f7-card :title="$t('title.floatTransfer')">
              <f7-card-content>
                <p>{{$t('text.bankTransfer')}}</p>
                <f7-row>
                  <f7-col>{{$t('label.accountNo')}}</f7-col>
                  <f7-col>{{company.accountNo}}</f7-col>
                </f7-row>
                <f7-row>
                  <f7-col>{{$t('label.sortCode')}}</f7-col>
                  <f7-col>{{company.sortCode}}</f7-col>
                </f7-row>
                <f7-row>
                  <f7-col>{{$t('label.reference')}}</f7-col>
                  <f7-col>{{company.name}}</f7-col>
                </f7-row>
              </f7-card-content>
            </f7-card>
            <f7-card :title="$t('label.address')">
              <f7-card-content>
                <f7-row>{{company.mailAddress.addressLine1}}</f7-row>
                <f7-row v-if="company.mailAddress.addressLine2">
                  {{company.mailAddress.addressLine2}}
                </f7-row>
                <f7-row>{{company.mailAddress.city}}</f7-row>
                <f7-row>{{company.mailAddress.country}}</f7-row>
                <f7-row>{{company.mailAddress.postcode}}</f7-row>
              </f7-card-content>
            </f7-card>
            <f7-card :title="$t('title.adminContact')">
              <f7-card-content>
                <f7-row>
                  <f7-col>{{$t('label.phone')}}</f7-col>
                  <f7-col>+{{company.adminPhone}}</f7-col>
                </f7-row>
                <f7-row>
                  <f7-col>{{$t('label.email')}}</f7-col>
                  <f7-col>{{company.adminEmail}}</f7-col>
                </f7-row>
              </f7-card-content>
            </f7-card>
            <f7-card :title="$t('label.registrationNo')" :content="company.registrationNo"/>
            <f7-card :title="$t('label.kybStatus')" :content="company.kybStatus"/>
          </f7-block>
        </f7-accordion-content>
      </f7-list-item>
      <f7-list-item :title="$t('label.spendingCategories')" :link="links.categories"/>
      <f7-list-item :title="$t('title.manageTags')" :link="links.manageTags" v-beta/>
      <f7-list-item accordion-item :title="$t('title.xeroIntegration')">
        <f7-accordion-content>
          <f7-block v-if="!xeroConnection">
            <p>{{$t('text.xeroIntegration')[0]}}</p>
            <p>{{$t('text.xeroIntegration')[1]}}</p>
            <p>{{$t('text.xeroIntegration')[2]}}</p>
            <p>
              <f7-button fill large @click="connectXero">{{$t('action.connectXero')}}</f7-button>
            </p>
          </f7-block>
          <f7-block v-else>
            <f7-card :title="$t('label.xeroOrganisation')">
              <f7-card-content>{{company.xeroOrg}}</f7-card-content>
            </f7-card>
            <f7-card :title="$t('label.xeroBankAccount')">
              <f7-card-content>aio* - Expense Account</f7-card-content>
            </f7-card>
            <f7-card :title="$t('label.xeroChartAccounts')">
              <f7-card-content>{{$t('text.xeroChartAccounts')}}</f7-card-content>
            </f7-card>
            <p>
              <f7-button fill large color="pink" @click="disconnectXero">{{$t('action.disconnectXero')}}</f7-button>
            </p>
          </f7-block>
        </f7-accordion-content>
      </f7-list-item>
      <f7-list-item accordion-item :title="$t('title.paymentDetails')" :accordion-item-opened="!hasValidBilling">
        <f7-icon v-if="!hasValidBilling" slot="media" f7="bolt_circle_fill" color="pink"/>
        <f7-accordion-content>
          <f7-block>
            <p v-html="$t('text.price')"></p>
            <f7-list v-if="billing.last4Digits">
              <f7-list-item header :title="$t('label.cardDetails')"/>
              <f7-list-item :title="$t('label.cardBrand')" :after="billing.brand"/>
              <f7-list-item :title="$t('label.cardNumber')" :after="`**** **** **** ${billing.last4Digits}`"/>
              <f7-list-item :title="$t('label.cardExpires')" :after="`${billing.expMonth} / ${billing.expYear}`">
                <f7-icon v-if="!hasValidBilling" slot="media" f7="bolt_circle_fill" color="pink"/>
              </f7-list-item>
            </f7-list>
            <p :class="{'text-color-pink': !hasValidBilling}">{{$t('text.validPayment')}}</p>
            <h4>{{$t('title.addCard')}}</h4>
            <div id="card-element" ref="cardElement"></div>
            <span class="invalid-feedback">{{stripeFeedback}}</span>
            <p>
              <f7-button @click="createStripeToken" fill large>{{$t('action.updateCard')}}</f7-button>
            </p>
          </f7-block>
        </f7-accordion-content>
      </f7-list-item>
      <f7-list-item accordion-item :title="$t('title.betaFeatures')">
        <f7-accordion-content>
          <f7-block>
            <p>
              {{$t('text.betaFeatures')}}
            </p>
          </f7-block>
          <f7-list>
            <list-input-toggle :label="$t('action.enableBeta')"
                               v-model="betaEnabled"
                               />
          </f7-list>
        </f7-accordion-content>
      </f7-list-item>
    </f7-list>
    <main-toolbar slot="fixed" :active="3"/>
  </f7-page>
</template>

<script>
  import api from 'aio-api'
  import cordovaHelpers from '@/utils/cordova-helpers'
  import helpers from '@/utils/helpers'
  import MainToolbar from '../components/main-toolbar'
  import ListInputToggle from '@/components/ListInputToggle'

  export default {
    name: 'company-page',
    components: {
      ListInputToggle,
      MainToolbar,
    },
    data () {
      return {
        company: {
          mailAddress: {}
        },
        stripeFeedback: '',
        xeroConnection: null,
      }
    },
    asyncMethods: {
      async connectXero () {
        window.open(await api.xero.connect(), this.hrefTarget, 'resizable=yes')
        // Go back to the root page so we're not reflecting a stale xero connection status.
        this.$f7.view.main.router.navigate(this.links.root)
      },
      async createStripeToken () {
        this.$bus.$emit('ajax-start')
        const stripeInput = this.$refs.cardElement.querySelector('input')
        this.stripeFeedback = ''
        const result = await this.stripe.createToken(this.card)
        if (result.error) {
          stripeInput.setCustomValidity(result.error.message)
          this.stripeFeedback = result.error.message
        } else {
          stripeInput.setCustomValidity('')
          await api.updateBilling({
            stripeToken: result.token.id,
            plan: 'monthly'
          })
          this.$store.dispatch('fetchBilling')
        }
        this.$bus.$emit('ajax-stop')
      },
      async disconnectXero () {
        await api.xero.disconnect()
        await this.checkXero()
      },
      async checkXero () {
        this.company = await api.getMyCompany()
        try {
          this.xeroConnection = null
          this.xeroConnection = await api.xero.check()
        } catch (err) {
          console.error('No xero connection', err)
        }
      }

    },
    async mounted () {
      await this.checkXero()
      this.$store.dispatch('fetchBilling')
      this.stripe = window.Stripe(process.env.VUE_APP_STRIPE_KEY)
      const elements = this.stripe.elements()
      const style = {
        base: {
          color: '#495057',
          fontFamily: 'Rubik, sans-serif',
          fontWeight: 400,
          fontSize: '16px',
          '::placeholder': {
            color: '#6c757d'
          }
        },
        invalid: {
          color: '#EE0156',
          iconColor: '#EE0156'
        }
      }
      this.card = elements.create('card', {style: style})
      this.card.mount('#card-element')
    },
    computed: {
      betaEnabled: {
        get () {
          return this.$store.state.beta.enabled
        },
        set (value) {
          this.$store.dispatch('beta/set', value)
        },
      },
    },
    mixins: [cordovaHelpers, helpers]
  }
</script>

<style lang="scss" scoped>
  @import '@/sass/variables.scss';

  #card-element {
    padding: 20px 0;
  }

  .invalid-feedback {
    color: $el-salmon;
  }
</style>
